<template>
  <b-card class="no-border animated">
    <div class="item-header clearfix mb-3">
      <h5 class="pull-left mb-0 mt-2">{{ header }}</h5>
    </div>
    <b-form @submit.prevent="onSubmit()">
      <legend>Detail</legend>
      <div class="form-row">
        <div class="form-group col-md-12">
          <label>Name</label>
          <input
            v-model.trim="form.name"
            type="text"
            placeholder="Enter company name"
            class="form-control"
            name="name"
            v-validate="'required'"
            :class="{'is-invalid': errors.has('name')}">
          <b-form-feedback>{{ errors.first('name') }}</b-form-feedback>
        </div>
        <div class="form-group col-md-6" v-if="form.type === 'DEFAULT'">
          <label for="input_namespace">Namespace</label>
          <input
            name="namespace"
            v-validate="'required'"
            type="text"
            v-model="form.namespace"
            class="form-control"
            id="input_namespace"
            placeholder="Namespace"
            :disabled="isEdit"
            @change="onNamespaceChange"
            :class="{'is-invalid': errors.has('namespace')}">
          <b-form-feedback>{{ errors.first('namespace') }}</b-form-feedback>
          <div :class="isDuplicateNamespace ? 'unavailable' : 'available'">Namespace is unavailable.</div>
        </div>
        <div class="form-group col-md-6" v-if="form.type === 'DEFAULT'">
          <label for="input_external_id">External ID</label>
          <input
            name="external_id"
            v-validate="'required'"
            type="text"
            v-model="form.external_id"
            class="form-control"
            id="input_external_id"
            placeholder="External ID"
            @change="onExternalIdChange"
            :disabled="isEdit"
            :class="{'is-invalid': errors.has('external_id')}">
          <b-form-feedback>{{ errors.first('external_id') }}</b-form-feedback>
          <div :class="isDuplicateExternalId ? 'unavailable' : 'available'">External ID is unavailable.</div>
        </div>
      </div>
      <fieldset class="form-group">
        <legend>Type</legend>
        <div
          class="form-row"
          :class="{'is-invalid': errors.has('company type'), 'no-border': !errors.has('company type')}">
          <legend class="col-form-legend col-sm-2">Company Type</legend>
          <div class="col-sm-10">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  class="form-check-input"
                  v-model="form.type"
                  v-validate="'required'"
                  type="radio"
                  name="company type"
                  value="MANUFACTURER"
                  checked>
                <span :class="{'font-weight-bold': isManufacturer}">Client</span>
                <small class="form-text text-muted">
                  This company as a client of your company.
                </small>
              </label>
            </div>
            <div class="form-check">
              <label class="form-check-label">
                <input
                  class="form-check-input"
                  v-model="form.type"
                  v-validate="'required'"
                  type="radio"
                  name="company type"
                  value="TRANSPORTER"
                  checked>
                <span :class="{'font-weight-bold': isTransporter}">Transporter</span>
                <small class="form-text text-muted">
                  This company as a vehicle provider.
                </small>
              </label>
            </div>
            <div class="form-check">
              <label class="form-check-label">
                <input
                  class="form-check-input"
                  v-model="form.type"
                  type="radio"
                  name="company type"
                  value="CUSTOMER">
                <span :class="{'font-weight-bold': isCustomer}">Customer</span>
                <small class="form-text text-muted">
                  This company as a customer of your company.
                </small>
              </label>
            </div>
            <div class="form-check">
              <label class="form-check-label">
                <input
                  class="form-check-input"
                  v-model="form.type"
                  type="radio"
                  name="company type"
                  value="DEFAULT">
                <span :class="{'font-weight-bold': isRegular}">Regular</span>
                <small class="form-text text-muted">
                  Regular Company.
                </small>
              </label>
            </div>
          </div>
        </div>
        <b-form-feedback>{{ errors.first('company type') }}</b-form-feedback>
      </fieldset>
      <legend>Address</legend>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="input_address_country">Country</label>
          <input
            name="country"
            v-validate="'required'"
            type="text"
            v-model="form.address.country"
            class="form-control"
            id="input_address_country"
            placeholder="Country"
            :class="{'is-invalid': errors.has('country')}">
          <b-form-feedback>{{ errors.first('country') }}</b-form-feedback>
        </div>
        <div class="form-group col-md-6">
          <label for="input_address_state">State</label>
          <input
            type="text"
            v-model="form.address.state"
            class="form-control"
            id="input_address_state"
            placeholder="State">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="input_address_city">City</label>
          <input
            type="text"
            v-model="form.address.city"
            class="form-control"
            id="input_address_city"
            placeholder="City">
        </div>
        <div class="form-group col-md-6">
          <label for="input_address_zip_code">Zip / Postal Code</label>
          <input
            type="text"
            v-model="form.address.zip_code"
            class="form-control"
            id="input_address_zip_code"
            placeholder="Zip / Postal Code">
        </div>
      </div>
      <b-form-group
        id="label_address_address1"
        label="Address 1"
        label-for="input_address_address1">
        <b-form-input
          id="input_address_address1"
          type="text"
          v-model="form.address.address1"
          placeholder="Address line 1"/>
      </b-form-group>
      <b-form-group
        id="label_address_address2"
        label="Address 2"
        label-for="input_address_address2">
        <b-form-input
          id="input_address_address2"
          type="text"
          v-model="form.address.address2"
          placeholder="Address line 2"/>
      </b-form-group>
      <hr>
      <div class="form-row">
        <div class="form-group col-md-6">
          <b-form-group
            id="notification_email"
            label="Notification Email"
            label-for="input_notification_email">
            <b-form-input
              id="input_notification_email"
              type="email"
              v-model="form.notification_email"
              placeholder="Notification Email"/>
          </b-form-group>
        </div>
        <div class="form-group col-md-6">
          <b-form-group
            id="company-timezone-label"
            label="Timezone"
            label-for="company-timezone">
            <multiselect
              id="company-timezone"
              v-model="selectedTimezone"
              label="text"
              track-by="value"
              placeholder="Select timezone"
              :options="timezoneOptions"
              :searchable="true">
            </multiselect>
          </b-form-group>
        </div>
      </div>
      <hr>
      <b-button
        type="submit"
        id="createBtn"
        :disabled="disabled"
        variant="primary">
        <span v-if="beforeClick">Save changes</span>
        <span v-else> Loading..</span>
        <i v-if="loadingSpinner" class="fa fa-spinner fa-spin"></i>
      </b-button>
      <b-button
        @click="onCancel()"
        type="reset"
        variant="outline-secondary"
        class="pull-right">Cancel</b-button>
    </b-form>
  </b-card>
</template>
<script>
import tmsApi from '@/api/tmsApi';
import Multiselect from 'vue-multiselect';
import { isEmpty, get, isUndefined } from 'lodash';
import config from '@/config';

export default {
  name: 'company_detail',
  components: { Multiselect },
  data() {
    return {
      form: {
        id: null,
        namespace: null,
        external_id: null,
        name: '',
        type: '',
        address: {
          country: '',
          state: '',
          city: '',
          zip_code: '',
        },
        notification_email: '',
        timezone: null,
        metadata: {},
      },
      selectedTimezone: null,
      loadingSpinner: false,
      beforeClick: true,
      disabled: false,
      timeoutNamespace: null,
      isDuplicateNamespace: false,
      timeoutExternalId: null,
      isDuplicateExternalId: false,
    };
  },
  created() {
    const { companyId } = this.$route.params;
    const action = companyId ? 'edit' : 'create';
    const breadcrumb = [
      { to: '', name: 'Home' },
      { to: 'admin', name: 'Admin' },
      { to: 'companies', name: 'Companies' },
      { to: companyId, name: 'Detail' },
      { to: 'edit', name: 'Edit' },
    ];
    const isEdit = action === 'edit';
    this.$store.dispatch('companies/LOAD_TIMEZONES').then(() => {
      if (!isEdit) {
        breadcrumb.splice(3, 2);
        breadcrumb.push({ to: 'create', name: 'Create' });
        this.selectedTimezone = this.timezoneOptions.find(dt => dt.value === this.defaultTimezone);
      } else {
        this.$store.commit('companies/SET_DETAIL_ID', companyId);
        this.$store.dispatch('companies/LOAD_DETAIL_DATA');
      }
    });

    this.$store.commit('SET_BREADCRUMB_ITEMS', breadcrumb);
  },
  computed: {
    detailedInfo() {
      const detailInfo = this.isEdit ? this.$store.getters['companies/get_detail_data'] : {};
      return detailInfo;
    },
    displayName() {
      return this.detailedInfo.name;
    },
    header() {
      const header = this.isEdit
        ? `Edit Company : ${this.displayName}`
        : 'Create New Company';
      return header;
    },
    companyType() {
      const { type } = this.detailedInfo;
      switch (type) {
        case 'TRANSPORTER':
          return 'Transporter';
        case 'CUSTOMER':
          return 'Customer';
        default:
          return 'Default';
      }
    },
    isTransporter() {
      return this.form.type === 'TRANSPORTER';
    },
    isCustomer() {
      return this.form.type === 'CUSTOMER';
    },
    isRegular() {
      return this.form.type === 'DEFAULT';
    },
    isManufacturer() {
      return this.form.type === 'MANUFACTURER';
    },
    isEdit() {
      return !isEmpty(this.$route.params.companyId);
    },
    timezoneOptions() {
      const timezones = this.$store.getters['companies/timezones'].map(dt => (
        {
          text: dt,
          value: dt,
        }
      ));
      return timezones;
    },
    defaultTimezone() {
      const { userData } = this.$store.getters;
      const timezone = get(userData, 'company.metadata.timezone', config.defaultTimezone);
      return timezone;
    },
  },
  watch: {
    detailedInfo(data) {
      const { form } = this;
      const { address } = form;
      const { metadata } = data;
      const notification_email = !isEmpty(metadata) ? get(metadata, 'notification_email', '') : '';
      const timezone = !isEmpty(metadata) ? get(metadata, 'timezone', '') : this.defaultTimezone;

      form.id = data.id;
      form.name = data.name;
      form.email = data.email;
      form.type = data.type;
      form.metadata = metadata;
      form.notification_email = notification_email;
      form.namespace = data.namespace || null;
      form.external_id = data.external_id || null;

      this.selectedTimezone = this.timezoneOptions.find(dt => dt.value === timezone);

      data.address = data.address || {};
      address.country = data.address.country;
      address.state = data.address.state;
      address.city = data.address.city;
      address.zip_code = data.address.zip_code;
      address.address1 = data.address.address1;
      address.address2 = data.address.address2;
    },
    selectedTimezone(data) {
      this.form.timezone = data.value;
    },
  },
  methods: {
    onSubmit() {
      this.validate().then((valid) => {
        let isNamespaceAndExtIdValid = true;

        if (!this.isEdit) {
          if (this.form.type === 'DEFAULT') {
            if (this.isDuplicateNamespace || this.isDuplicateExternalId) {
              isNamespaceAndExtIdValid = false;
            }
          }
        }

        if (valid && isNamespaceAndExtIdValid) {
          this.loadingSpinner = true;
          this.beforeClick = false;
          this.disabled = true;
          let { companyId } = this.$route.params;
          const { parent_id: parentId, type } = this.$route.params;
          const mainAction = this.isEdit ? 'UPDATE_DATA' : 'CREATE_DATA';
          const action = type === 'Subsidiary' ? 'CREATE_SUBSIDIARY' : mainAction;

          if (action === 'CREATE_SUBSIDIARY') this.form.parent = parentId;

          this.$store.dispatch(`companies/${action}`, this.form).then((company) => {
            companyId = this.isEdit ? companyId : company.id;
            this.$router.push(`/admin/companies/${companyId}`);
          });
        } else {
          this.loadingSpinner = false;
          this.beforeClick = true;
          this.disabled = false;
        }
      });
    },
    validate() {
      return this.$validator.validateAll();
    },
    onCancel() {
      const { companyId } = this.$route.params;
      if (this.isEdit) {
        this.$router.push(`/admin/companies/${companyId}`);
      } else {
        this.$router.push('/admin/companies');
      }
    },
    onNamespaceChange(e) {
      if (this.timeoutNamespace) clearTimeout(this.timeoutNamespace);
      this.timeoutNamespace = setTimeout(() => {
        const namespace = e.target.value;
        if (this.isEdit) {
          if (this.detailedInfo.namespace === namespace) return;
        }
        tmsApi.check_namespace(this.token, { namespace }).then((resp) => {
          if (isUndefined(resp.error)) {
            this.isDuplicateNamespace = !resp.available;
          }
        });
      }, 5e2);
    },
    onExternalIdChange(e) {
      if (this.timeoutExternalId) clearTimeout(this.timeoutExternalId);
      this.timeoutExternalId = setTimeout(() => {
        const externalId = e.target.value;
        if (this.isEdit) {
          if (this.detailedInfo.external_id === externalId) return;
        }
        tmsApi.check_external_id(this.token, { external_id: externalId }).then((resp) => {
          if (isUndefined(resp.error)) {
            this.isDuplicateExternalId = !resp.available;
          }
        });
      }, 5e2);
    },
  },
};
</script>
<style>
.multiselect {
  height: 35px;
  min-height: 35px;
  max-height: 35px;
}

.multiselect__select {
  height: 35px;
  min-height: 35px;
  max-height: 35px;
}

.multiselect__tags {
  height: 35px;
  min-height: 35px;
  max-height: 35px;
  padding: 5px 40px 0 8px;
}

.multiselect__placeholder {
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 5px;
}

.unavailable {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f86c6b;
}

.available {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/views/pages/admin/companies/CreateEdit.vue